/* eslint-disable max-len */
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './UsersTable.scss';
import { mainColors } from '../../helpers/colors';
import { useTranslation } from '../../hooks/useTranslation';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CAvatar,
  CRow,
  CCol,
  CFormInput,
  CCollapse
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, 
    cilPencil, cilTrash, cilUser, 
    cilEnvelopeClosed, cilPhone, cilLockLocked, cilLowVision, cilFilter, cilX, cilSearch } from '@coreui/icons';
import {
  updateUserRequestAction,
  deleteUserRequestAction,
  resetUserPasswordRequestAction,
  getUserDetailsRequestAction
} from '../../store/users/usersActions';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

// getFullName fonksiyonunu UserTable fonksiyonunun dışına taşı
const getFullName = (item) => {
  const name = item.Name || '';
  const surname = item.Surname || '';
  return `${name} ${surname}`.trim() || '-';
};

function UserTable(props) {
  const { 
    userList,
    userData,
    changeSortType,
    changeSortKey,
    userHistorySortData,
    updateUserRequest,
    deleteUserRequest,
    resetUserPasswordRequest,
    resetUserPasswordData,
    getUserDetailsRequest
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(
    ['index','UserId','fullName','UserName','Email','Status','LastLoginDatetime','actions'],
    'UsersTable'
  );
  
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [resetUserPasswordModal, setResetPasswordModal] = useState({ open: false, item: null });
  const [resetUserPasswordResult, setResetPasswordResult] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    UserId: '',
    fullName: '',
    UserName: '',
    Email: '',
    Status: '',
    LastLoginDatetime: ''
  });

  useEffect(() => {
    if (userList && Array.isArray(userList)) {
      setTotalRows(userList.length);
      setTableData(userList);
    }
  }, [userList]);

  useEffect(() => {
    if (resetUserPasswordData) {
      setResetPasswordResult(resetUserPasswordData);
    }
  }, [resetUserPasswordData]);

  const handleEdit = (item) => {
    navigate(`/users/edit/${item.UserId}`, {
      state: { user: item }
    });
  };

  const handleViewDetails = (item) => {
    navigate(`/users/details/${item.UserId}`, {
      state: { user: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleResetPasswordClick = (item) => {
    setResetPasswordModal({ open: true, item });
    setResetPasswordResult(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          UserId: deleteModal.item.UserId,
          Status: 0,
          UserName: deleteModal.item.UserName,
          Email: deleteModal.item.Email,
          Name: deleteModal.item.Name,
          Surname: deleteModal.item.Surname,
          CepTel: deleteModal.item.CepTel || '',
          Telefon: deleteModal.item.Telefon || ''
        };
        deleteUserRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleResetPasswordConfirm = () => {
    if (resetUserPasswordModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const resetData = {
          SessionUserId: String(userId),
          UserId: String(resetUserPasswordModal.item.UserId) 
        };
        resetUserPasswordRequest({ data: resetData });
      } else {
        console.error('User session not found');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordModal({ open: false, item: null });
    setResetPasswordResult(null);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    scrollToTableContainer();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (changeSortType) changeSortType(newOrder);
    if (changeSortKey) changeSortKey(property);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      UserId: '',
      fullName: '',
      UserName: '',
      Email: '',
      Status: '',
      LastLoginDatetime: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.user-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Filtrelenmiş verileri hesapla
  const filteredData = useMemo(() => {
    return tableData.filter(item => {
      return Object.keys(filters).every(key => {
        const filterValue = filters[key].toLowerCase();
        if (!filterValue) return true;
        
        if (key === 'fullName') {
          const fullName = getFullName(item).toLowerCase();
          return fullName.includes(filterValue);
        }
        
        const itemValue = item[key]?.toString().toLowerCase() || '';
        return itemValue.includes(filterValue);
      });
    });
  }, [tableData, filters]);

  const getSortedData = () => {
    const stabilizedData = stableSort(filteredData, getComparator(order, orderBy));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  // const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginationItems = [];

  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem
        key={i}
        active={i === page}
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }

  const getUserStatusBadge = (status) => {
    const statusMap = {
      1: { color: 'success', text: t('activeUser') || 'Aktif' },
      0: { color: 'secondary', text: t('inactiveUser') || 'İnaktif' }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status };
    return <CBadge color={statusInfo.color} shape="rounded-pill">{statusInfo.text}</CBadge>;
  };

  const formatDate = (dateTime) => {
    if (!dateTime || dateTime === "0") return '-';
    
    const dateStr = String(dateTime).trim();
    

    if (dateStr.length === 16) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);

      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {

        const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        return utcPlus3Date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    

    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {
        // UTC+3 saat dilimi ekle
        const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        return utcPlus3Date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    
    return '-';
  };

  const columns = [
    { id: 'index', label: t('userId') || 'Kullanıcı ID', sortable: false, filterable: false },
    { id: 'UserId', label: t('userId') || 'Kullanıcı ID', sortable: true, filterable: true },
    { id: 'fullName', label: t('userFullName') || 'Ad Soyad', sortable: true, filterable: true },
    { id: 'UserName', label: t('userName') || 'Kullanıcı Adı', sortable: true, filterable: true },
    { id: 'Email', label: t('userEmail') || 'E-posta', sortable: true, filterable: true },
    { id: 'Status', label: t('userStatus') || 'Durum', sortable: true, filterable: true },
    { id: 'LastLoginDatetime', label: t('userLastLogin') || 'Son Giriş', sortable: true, filterable: true },
    { id: 'actions', label: t('Actions') || 'İşlemler', sortable: false, filterable: false }
  ];

  const activeFilterCount = Object.values(filters).filter(value => value.trim() !== '').length;

  const exportColumns = useMemo(() => {
    return columns.filter(col => !['actions', 'index'].includes(col.id));
  }, [columns]);

  const buildCellValue = (colId, item) => {
    if (colId === 'fullName') return getFullName(item);
    if (colId === 'LastLoginDatetime') return formatDate(item.LastLoginDatetime);
    if (colId === 'Status') {
      const status = item.Status;
      return status === 1 ? (t('activeUser') || 'Aktif') : status === 0 ? (t('inactiveUser') || 'İnaktif') : String(status);
    }
    return item[colId] ?? '';
  };

  return (
    <div className="user-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                  {tableData.length !== totalRows && (
                    <span className="filtered-info ms-1">
                      ({tableData.length} {t('totalRecords')})
                    </span>
                  )}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={getSortedData()}
                  allRows={stableSort(tableData, getComparator(order, orderBy))}
                  buildCellValue={buildCellValue}
                  filenamePrefix="users_export"
                  triggerDisabled={totalRows === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {columns.filter(col => col.filterable).map((column) => (
                  <CCol key={column.id} xs={12} sm={6} md={4} lg={3}>
                    <div className="filter-field">
                      <label className="filter-label">{column.label}</label>
                      <div className="filter-input-wrapper">
                        <CIcon icon={cilSearch} className="filter-input-icon" />
                        {column.id === 'Status' ? (
                          <select
                            className="filter-input filter-select form-select form-select-sm"
                            value={filters.Status}
                            onChange={e => handleFilterChange('Status', e.target.value)}
                          >
                            <option value="">{t('allStatuses') || 'Tümü'}</option>
                            <option value="1">{t('activeUser') || 'Aktif'}</option>
                            <option value="0">{t('inactiveUser') || 'Pasif'}</option>
                          </select>
                        ) : (
                          <CFormInput
                            type="text"
                            placeholder={`${t('search')} ${column.label.toLowerCase()}...`}
                            value={filters[column.id]}
                            onChange={(e) => handleFilterChange(column.id, e.target.value)}
                            className="filter-input"
                            size="sm"
                          />
                        )}
                        {filters[column.id] && column.id !== 'Status' && (
                          <CButton
                            color="link"
                            size="sm"
                            className="filter-clear-btn"
                            onClick={() => handleFilterChange(column.id, '')}
                          >
                            <CIcon icon={cilX} size="sm" />
                          </CButton>
                        )}
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell
                      key={column.id}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                      className="table-header-cell"
                    >
                      <div className="d-flex align-items-center">
                        {column.id === 'index' ? '#' : column.label}
                        {column.sortable && orderBy === column.id && (
                          <CIcon
                            icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                            className="ms-1"
                          />
                        )}
                      </div>
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.UserId || index}>
                    <CTableDataCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.UserId}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex align-items-center">
                        <CAvatar 
                          color="primary" 
                          textColor="white"
                          className="me-2"
                          size="sm"
                        >
                          {item.Name?.charAt(0)?.toUpperCase() || <CIcon icon={cilUser} />}
                        </CAvatar>
                        <div>
                          <div className="fw-semibold">{getFullName(item)}</div>
                          {(item.CepTel || item.Telefon) && (
                            <small className="text-muted d-flex align-items-center">
                              <CIcon icon={cilPhone} size="sm" className="me-1" />
                              {item.CepTel || item.Telefon}
                            </small>
                          )}
                        </div>
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="fw-medium">{item.UserName}</div>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex align-items-center">
                        <CIcon icon={cilEnvelopeClosed} size="sm" className="me-1 text-muted" />
                        {item.Email}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>
                      {getUserStatusBadge(item.Status)}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span className="text-muted">
                        {formatDate(item.LastLoginDatetime)}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('viewUserDetails') || 'Kullanıcı Detaylarını Görüntüle'}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleViewDetails(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilLowVision}
                              size="sm"
                              style={{ color: '#6c757d' }}
                            />
                          </div>
                        </CTooltip>

                        <CTooltip content={t('editUser') || 'Kullanıcıyı Düzenle'}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleEdit(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilPencil}
                              size="sm"
                              style={{ color: mainColors.primary }}
                            />
                          </div>
                        </CTooltip>

                        <CTooltip content={t('resetUserPassword') || 'Şifre Sıfırla'}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleResetPasswordClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilLockLocked}
                              size="sm"
                              style={{ color: '#fd7e14' }}
                            />
                          </div>
                        </CTooltip>
                        
                        <CTooltip content={t('deleteUser') || 'Kullanıcıyı Sil'}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleDeleteClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilTrash}
                              size="sm"
                              style={{ color: '#dc3545' }}
                            />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={8} className="text-center py-4">
                    <div className="no-data-message">
                      {userData?.Result?.ErrorCode === 0 ? (
                        activeFilterCount > 0 ? (
                          <div className="no-results-message">
                            <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                            <div>{t('noResultsFound')}</div>
                            <CButton
                              color="link"
                              size="sm"
                              onClick={clearFilters}
                              className="mt-2"
                            >
                              {t('clearFiltersToSeeAll')}
                            </CButton>
                          </div>
                        ) : (
                          <div className="text-muted">{t('noDataFound') || 'Veri bulunamadı'}</div>
                        )
                      ) : (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData') || 'Veri yükleniyor...'}
                        </div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show') || 'Göster'}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries') || 'kayıt'}</span>
              </div>
              
              <CPagination className="mb-0">
                <CPaginationItem
                  onClick={() => page > 1 && handleChangePage(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem
                  onClick={() => page < totalPages && handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>

              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Delete User Modal */}
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteUser') || 'Kullanıcıyı Sil'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteUser') || 'Bu kullanıcıyı silmek istediğinize emin misiniz?'}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('userFullName') || 'Ad Soyad'}:</strong> {getFullName(deleteModal.item)}
              <br/>
              <strong>{t('userName') || 'Kullanıcı Adı'}:</strong> {deleteModal.item.UserName}<br/>
              <strong>{t('userEmail') || 'E-posta'}:</strong> {deleteModal.item.Email}<br/>
              <strong>ID:</strong> {deleteModal.item.UserId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('userDeleteWarning') || 'Bu işlem geri alınamaz!'}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('cancel') || 'İptal'}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('delete') || 'Sil'}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Reset Password Modal */}
      <CModal visible={resetUserPasswordModal.open} onClose={handleResetPasswordCancel}>
        <CModalHeader>
          <CModalTitle>{t('resetUserPassword') || 'Şifre Sıfırla'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {!resetUserPasswordResult ? (
            <>
              <p>{t('confirmResetPassword') || 'Bu kullanıcının şifresini sıfırlamak istediğinize emin misiniz?'}</p>
              {resetUserPasswordModal.item && (
                <div className="bg-light p-3 rounded">
                  <strong>{t('userFullName') || 'Ad Soyad'}:</strong> {getFullName(resetUserPasswordModal.item)}
                  <br/>
                  <strong>{t('userName') || 'Kullanıcı Adı'}:</strong> {resetUserPasswordModal.item.UserName}<br/>
                  <strong>{t('userEmail') || 'E-posta'}:</strong> {resetUserPasswordModal.item.Email}<br/>
                  <strong>ID:</strong> {resetUserPasswordModal.item.UserId}
                </div>
              )}
            </>
          ) : (
            <div>
              {resetUserPasswordResult.Result && resetUserPasswordResult.Result.ErrorCode === 0 ? (
                <div className="alert alert-success">
                  <h6 className="alert-heading">{t('passwordResetSuccess') || 'Şifre Başarıyla Sıfırlandı!'}</h6>
                  <p className="mb-2">{t('passwordResetSuccessMessage') || 'Kullanıcının şifresi başarıyla sıfırlandı.'}</p>
                  {resetUserPasswordResult.ActivationCode && (
                    <div className="bg-white p-3 rounded border mt-3">
                      <strong>{t('newPassword') || 'Yeni Şifre'}:</strong>
                      <div className="mt-2">
                        <code className="bg-dark text-light p-2 rounded d-block" style={{ fontSize: '16px', letterSpacing: '1px' }}>
                          {resetUserPasswordResult.ActivationCode}
                        </code>
                      </div>
                      <small className="text-muted mt-2 d-block">
                        {t('newPasswordNote') || 'Bu şifreyi güvenli bir yerde saklayın ve kullanıcıya iletin.'}
                      </small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="alert alert-danger">
                  <h6 className="alert-heading">{t('passwordResetError') || 'Şifre Sıfırlama Hatası!'}</h6>
                  <p className="mb-0">
                    {resetUserPasswordResult.Result?.ErrorDescription || t('passwordResetErrorMessage') || 'Şifre sıfırlanırken bir hata oluştu.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          {!resetUserPasswordResult ? (
            <>
              <CButton color="secondary" onClick={handleResetPasswordCancel}>
                {t('cancel') || 'İptal'}
              </CButton>
              <CButton color="warning" onClick={handleResetPasswordConfirm}>
                {t('resetUserPassword') || 'Şifre Sıfırla'}
              </CButton>
            </>
          ) : (
            <CButton color="primary" onClick={handleResetPasswordCancel}>
              {t('close') || 'Kapat'}
            </CButton>
          )}
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    userList: state.usersReducer?.userList || [],
    userData: state.usersReducer?.userData || null,
    userHistorySortData: state.usersReducer?.userHistorySortData || {},
    resetUserPasswordData: state.usersReducer?.resetUserPasswordData || null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUserRequest: (payload) => {
      dispatch(updateUserRequestAction(payload));
    },
    deleteUserRequest: (payload) => {
      dispatch(deleteUserRequestAction(payload));
    },
    resetUserPasswordRequest: (payload) => {
      dispatch(resetUserPasswordRequestAction(payload));
    },
    getUserDetailsRequest: (payload) => {
      dispatch(getUserDetailsRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserTable);